import { z } from 'zod'
import { ToekenningId } from './id.js'
import { Moment, Person } from './moment.js'

/**
 * Een opening door functioneel beheer (R-155f, R-155g, R-66b, ontwerp 4.8, 9.2 en 9.4).
 *
 * Functioneel beheer kan een instantie openen waarop het geen toekenning heeft, alleen met een
 * reden (een categorie en een interne toelichting) en een meldingsnummer. Dat geeft een toekenning
 * met bron beheer: alleen lezen, voor het dossier en het bouwblok van die instantie, met een einde
 * na een vaste tijd. Die toekenning is zelf het auditlog: ze wordt nooit verwijderd of gewijzigd.
 *
 * **De vaste lijst staat op de invoer en niet op de opgeslagen toekenning.** Een pod parset
 * `Toekenning` strikt en wordt nooit bijgewerkt (D-36). Kwam er ooit een categorie bij, dan zou een
 * oude pod op hetzelfde dossier en blok vallen over een toekenning die een nieuwere pod schreef. Dus:
 * wat er binnenkomt moet uit de lijst van vandaag komen, wat er is opgeslagen is een gevulde tekst.
 */

/**
 * De categorieën van een reden (R-155f). De aanvrager ziet de categorie (R-66b); de toelichting
 * blijft intern.
 *
 * Drie, uit de besluitnotitie van 9 oktober 2026 en het voorbeeld in koppelvlak hoofdstuk 3. Een
 * integriteitsonderzoek staat er met opzet niet in: dat loopt niet via deze opening maar via een
 * eigen procedure (R-155g).
 */
export const REASON_CATEGORIES = ['storing-gemeld-door-aanvrager', 'technische-controle', 'verzoek-van-nwo'] as const
export const ReasonCategory = z.enum(REASON_CATEGORIES)
export type ReasonCategory = z.infer<typeof ReasonCategory>

/**
 * Hoe lang een opening duurt: één uur.
 *
 * De orde van grootte uit de besluitnotitie van 9 oktober 2026 (ontwerp 12.2): lang genoeg om een
 * gemelde storing na te lopen, kort genoeg dat een vergeten opening niet de rest van de dag openstaat.
 * Wie langer nodig heeft opent opnieuw, met een eigen reden, en dan staat dat ook als tweede regel in
 * het auditlog. **De duur staat nog open bij NWO** (ontwerp 12.2); dit is de waarde waarmee gebouwd
 * is, en hem wijzigen raakt alleen nieuwe openingen.
 */
export const ADMIN_OPENING_DURATION_MS = 60 * 60 * 1000

/** Een vrije tekst die er moet zijn: na weghalen van witruimte niet leeg. */
const Filled = z.string().trim().min(1)

/**
 * Wat de beheerder bij een opening opgeeft (R-155f). Wie hij is en aan welke kant hij staat komen
 * uit het token en nooit uit het verzoek (R-154), dus die staan hier niet in en worden geweigerd.
 */
export const AdminOpeningRequest = z.strictObject({
  redencategorie: ReasonCategory,
  /** Intern: de aanvrager ziet hem nooit (R-66b). */
  toelichting: Filled.max(2000),
  meldingsnummer: Filled.max(64),
})
export type AdminOpeningRequest = z.infer<typeof AdminOpeningRequest>

/**
 * Een opening zoals de interne kant haar in het overzicht ziet (R-155g): wie, wanneer, de reden met
 * toelichting, het meldingsnummer en tot wanneer.
 */
export const AdminOpening = z.strictObject({
  /** De toekenning met bron beheer: zij is de regel in het auditlog. */
  id: ToekenningId,
  moment: Moment,
  redencategorie: Filled,
  persoon: Person,
  toelichting: Filled,
  meldingsnummer: Filled,
  tot: Moment,
})
export type AdminOpening = z.infer<typeof AdminOpening>

/**
 * Een opening zoals de externe kant haar ziet (R-66b): de datum en de categorie, en verder niets.
 *
 * Geen naam van de beheerder en geen meldingsnummer, en ook de toelichting niet, want die is intern.
 * Dat volgt het inzagerecht zoals het Hof van Justitie het uitlegt in zaak C-579/21 (Pankki S, 22
 * juni 2023): de datum en het doel van een raadpleging, in principe niet de identiteit van de
 * medewerker (ontwerp 4.8). Strikt, zodat een veld dat er niet hoort niet stil meereist.
 */
export const AdminOpeningForExternal = z.strictObject({
  moment: Moment,
  redencategorie: Filled,
})
export type AdminOpeningForExternal = z.infer<typeof AdminOpeningForExternal>
